import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ArrowLeft, Users, Gamepad2, BarChart3, Settings, Bell,
  Search, Shield, Crown, Trash2, Eye, Ban, Check, X,
  DollarSign, TrendingUp, Activity, Globe, Loader2, Send,
  ToggleLeft, Rocket, Palette, Layers, Info, Tag, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import AdminTagsManager from '../components/AdminTagsManager';
import AdminAIKeysTab from '../components/AdminAIKeysTab';
import NotificationsBell from '../components/NotificationsBell';

function BulkRegenButton() {
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState(null); // {done, total}
  const run = async () => {
    if (busy) return;
    if (!window.confirm('Regenerate all template thumbnails via fal.ai (with OpenAI fallback)? This may take 30–60 seconds.')) return;
    setBusy(true);
    setProgress({ done: 0, total: 5, label: 'Contacting fal.ai…' });
    try {
      const r = await axios.post(`${API}/admin/templates/regenerate_thumbnails`, {}, { withCredentials: true });
      const items = r.data?.regenerated || [];
      const ok = items.filter(x => x.url).length;
      const failed = items.filter(x => x.error).length;
      setProgress({ done: ok, total: items.length, label: failed ? `${ok} regenerated, ${failed} failed` : 'Complete' });
      toast.success(`${ok}/${items.length} thumbnails regenerated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regeneration failed');
      setProgress(null);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <Button onClick={run} disabled={busy} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 h-8 text-xs" data-testid="admin-bulk-regen-btn">
        {busy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Regenerating…</> : <>Regenerate Template Thumbnails</>}
      </Button>
      {progress && (
        <span className="text-xs font-mono text-zinc-400" data-testid="admin-bulk-regen-progress">
          {progress.done}/{progress.total} · {progress.label}
        </span>
      )}
    </div>
  );
}
import axios from 'axios';
import { AdminFlags, AdminContent, AdminPromokit, AdminLogokit, AdminTrash } from '../components/AdminExtras';
import { AdminHelp } from '../components/AdminHelp';
import { AdminEmailsTab, AdminMarketplaceTab } from '../components/AdminNewTabs';
import { AdminHooksTab, AdminTelemetryTab, AdminDiagnosticsTab } from '../components/AdminEngineTabs';
import { OpenAccessCard } from '../components/OpenAccessCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [gameFilter, setGameFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [waveStatus, setWaveStatus] = useState(null);
  const [waveDismissed, setWaveDismissed] = useState(false);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/admin/gamegen/wave_status`, { withCredentials: true });
        if (!alive) return;
        if (!r.data?.never_run) setWaveStatus(r.data);
        setTimeout(tick, r.data?.running ? 8000 : 30000);
      } catch { if (alive) setTimeout(tick, 30000); }
    };
    tick();
    const onVis = async () => {
      if (document.hidden) return;
      // Instant refresh when the tab wakes from sleep/power-saving
      try {
        const r = await axios.get(`${API}/admin/gamegen/wave_status`, { withCredentials: true });
        if (!r.data?.never_run) setWaveStatus(r.data);
      } catch { /* next poll covers it */ }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; document.removeEventListener('visibilitychange', onVis); };
  }, []);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Notification state
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, gamesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/games`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setGames(gamesRes.data.games || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role }, { withCredentials: true });
      setUsers(users.map(u => u.user_id === userId ? { ...u, role } : u));
      toast.success(`User role updated to ${role}`);
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const updateUserTier = async (userId, tier) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/tier`, { tier }, { withCredentials: true });
      setUsers(users.map(u => u.user_id === userId ? { ...u, tier } : u));
      toast.success(`User tier updated to ${tier}`);
    } catch (error) {
      toast.error('Failed to update user tier');
    }
  };

  const toggleGamePublished = async (projectId, published) => {
    try {
      await axios.put(`${API}/admin/games/${projectId}`, { published }, { withCredentials: true });
      setGames(games.map(g => g.project_id === projectId ? { ...g, published } : g));
      toast.success(published ? 'Game published' : 'Game unpublished');
    } catch (error) {
      toast.error('Failed to update game');
    }
  };

  const sendNotification = async () => {
    if (!notificationTitle || !notificationMessage) {
      toast.error('Please fill in all fields');
      return;
    }
    setSendingNotification(true);
    try {
      await axios.post(`${API}/admin/notifications`, {
        title: notificationTitle,
        message: notificationMessage
      }, { withCredentials: true });
      toast.success('Notification sent to all users!');
      setNotificationTitle('');
      setNotificationMessage('');
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'users', name: 'Users', icon: Users },
    { id: 'admins', name: 'Admin Emails', icon: Shield },
    { id: 'games', name: 'Games', icon: Gamepad2 },
    { id: 'marketplace', name: 'Marketplace', icon: DollarSign },
    { id: 'aikeys', name: 'AI Keys', icon: Zap },
    { id: 'tags', name: 'Tags', icon: Tag },
    { id: 'flags', name: 'Feature Flags', icon: ToggleLeft },
    { id: 'content', name: 'Site Content', icon: Layers },
    { id: 'promokit', name: 'Promo Kit', icon: Rocket },
    { id: 'logokit', name: 'Logo Kit', icon: Palette },
    { id: 'trash', name: 'Trash', icon: Trash2 },
    { id: 'help', name: 'Help & Setup', icon: Info },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'hooks', name: 'Capability Hooks', icon: Layers },
    { id: 'telemetry', name: 'Telemetry', icon: BarChart3 },
    { id: 'diagnostics', name: 'Diagnostics', icon: Info },
  ];

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGames = games.filter(g =>
    g.name?.toLowerCase().includes(searchQuery.toLowerCase())
  ).filter(g => gameFilter === 'archived' ? g.archived : gameFilter === 'active' ? !g.archived : true);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#06B6D4] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <div className="w-64 bg-[#0A0A0C] border-r border-[#1E1E24] flex flex-col">
        <div className="p-4 border-b border-[#1E1E24]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white">Admin Panel</h2>
              <p className="text-xs text-zinc-500">PlayMold</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <li key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`admin-tab-${tab.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-[#06B6D4]/20 text-[#06B6D4]' 
                        : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-[#1E1E24]">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full justify-start text-zinc-400">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to App
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-16 border-b border-[#1E1E24] bg-[#0A0A0C] flex items-center justify-between px-6">
          <h1 className="text-xl font-bold text-white capitalize">{tabs.find(t => t.id === activeTab)?.name || activeTab}</h1>
          <div className="flex items-center gap-4">
            <NotificationsBell direction="down" />
            <Button
              variant="outline"
              size="sm"
              className="bg-transparent border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 text-xs"
              onClick={async () => {
                if (!window.confirm('Generate a fresh wave of 3 NEW AI studio games for the gallery?\n\nRuns in the background (~1-3 min per game). You will get a completion report here.')) return;
                try {
                  await axios.post(`${API}/admin/gamegen/reseed`, {}, { withCredentials: true });
                  toast.success('Fresh wave started — 3 brand-new games generating…');
                  let tries = 0;
                  const poll = async () => {
                    tries += 1;
                    try {
                      const r = await axios.get(`${API}/admin/gamegen/wave_status`, { withCredentials: true });
                      const st = r.data || {};
                      if (st.running && tries < 45) { setTimeout(poll, 20000); return; }
                      const ok = (st.results || []).filter(x => x.ok);
                      const bad = (st.results || []).filter(x => !x.ok);
                      if (ok.length) toast.success(`Fresh wave done — ${ok.length} new game${ok.length > 1 ? 's' : ''} published to the gallery!`);
                      bad.forEach(b => toast.error(`Wave game failed: ${b.error || 'unknown error'}`, { duration: 12000 }));
                      if (!ok.length && !bad.length) toast.error('Fresh wave produced no results — check server logs / provider keys');
                    } catch { if (tries < 45) setTimeout(poll, 20000); }
                  };
                  setTimeout(poll, 25000);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to start fresh wave');
                }
              }}
              data-testid="admin-fresh-wave-btn"
              title="Generate 3 new AI studio games to headline the gallery"
            >
              ⚡ Generate Fresh Wave
            </Button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64 bg-[#1E1E24] border-[#2E2E34] text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">{user?.name}</span>
              <div className="w-8 h-8 rounded-full bg-[#06B6D4] flex items-center justify-center text-black font-bold">
                {user?.name?.[0] || 'A'}
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-6 overflow-auto">
          {waveStatus && (waveStatus.running || (waveStatus.results || []).length > 0) && !waveDismissed && (
            <div className="mb-5 bg-[#111113] border border-[#06B6D4]/30 rounded-lg p-3" data-testid="wave-progress-panel">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono uppercase tracking-widest text-[#06B6D4]">
                  ⚡ Fresh Wave {waveStatus.running ? '— generating…' : '— complete'}
                </span>
                {!waveStatus.running && (
                  <button className="ml-auto text-zinc-500 hover:text-white text-xs" onClick={() => setWaveDismissed(true)} data-testid="wave-panel-dismiss">✕</button>
                )}
              </div>
              <div className="space-y-1.5">
                {(waveStatus.results || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] font-mono" data-testid={`wave-result-${i}`}>
                    <span className={r.ok ? 'text-green-400' : 'text-red-400'}>{r.ok ? '✓' : '✗'}</span>
                    <span className="text-zinc-300 truncate flex-1">{r.prompt}</span>
                    {r.ok
                      ? <a href={`/play/${r.project_id}`} target="_blank" rel="noreferrer" className="text-[#06B6D4] hover:underline shrink-0">play ↗</a>
                      : <span className="text-red-400/80 truncate max-w-[320px]" title={r.error}>{r.error}</span>}
                  </div>
                ))}
                {waveStatus.running && (
                  <div className="flex items-center gap-2 text-[11px] font-mono" data-testid="wave-current-row">
                    <span className="text-[#06B6D4] animate-pulse">●</span>
                    <span className="text-zinc-300 flex-1 truncate">
                      {(waveStatus.results || []).length}/3 done · rest generating in parallel — {waveStatus.current_job?.stage || 'starting'}…
                    </span>
                    <div className="w-40 h-1.5 bg-[#1E1E24] rounded overflow-hidden shrink-0">
                      <div className="h-full bg-[#06B6D4] transition-all duration-700" style={{ width: `${waveStatus.current_job?.progress || 5}%` }} />
                    </div>
                    <span className="text-zinc-500 w-9 text-right">{waveStatus.current_job?.progress || 5}%</span>
                  </div>
                )}
                {waveStatus.running && (
                  <p className="text-[9px] text-zinc-600">Runs on the server — you can close this page, sleep the laptop, and come back; it keeps going.</p>
                )}
              </div>
            </div>
          )}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <OpenAccessCard />
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#06B6D4]/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#06B6D4]" />
                    </div>
                    <span className="text-sm text-zinc-400">Total Users</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
                </div>
                <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Gamepad2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    <span className="text-sm text-zinc-400">Total Games</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats?.total_projects || 0}</p>
                </div>
                <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-purple-400" />
                    </div>
                    <span className="text-sm text-zinc-400">Published</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats?.published_games || 0}</p>
                </div>
                <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-amber-400" />
                    </div>
                    <span className="text-sm text-zinc-400">Revenue</span>
                  </div>
                  <p className="text-3xl font-bold text-white">$0</p>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {users.slice(0, 5).map((u, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-[#1E1E24] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#06B6D4]/20 flex items-center justify-center text-[#06B6D4] font-bold">
                          {u.name?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-sm text-white">{u.name || 'Unknown'}</p>
                          <p className="text-xs text-zinc-500">{u.email}</p>
                        </div>
                      </div>
                      <span className="text-xs text-zinc-500">{u.tier || 'free'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#1E1E24]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">User</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Role</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Tier</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Publish</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1E1E24]">
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="hover:bg-[#1E1E24]/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#06B6D4]/20 flex items-center justify-center text-[#06B6D4] font-bold">
                              {u.name?.[0] || '?'}
                            </div>
                            <div>
                              <p className="text-sm text-white">{u.name || 'Unknown'}</p>
                              <p className="text-xs text-zinc-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={u.role || 'user'}
                            onChange={(e) => updateUserRole(u.user_id, e.target.value)}
                            className="bg-[#1E1E24] border border-[#2E2E34] text-white text-sm rounded px-2 py-1"
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={u.tier || 'free'}
                            onChange={(e) => updateUserTier(u.user_id, e.target.value)}
                            className="bg-[#1E1E24] border border-[#2E2E34] text-white text-sm rounded px-2 py-1"
                          >
                            <option value="free">Free</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={async () => {
                              try {
                                const next = !u.can_publish;
                                await axios.put(`${API}/admin/users/${u.user_id}/publish-permission`, { can_publish: next }, { withCredentials: true });
                                setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, can_publish: next } : x));
                                toast.success(next ? `${u.name || u.email} can now publish to the catalogue` : `Publishing revoked — their games were pulled from the catalogue`);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to update permission');
                              }
                            }}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${u.can_publish ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-zinc-800 border-[#2E2E34] text-zinc-500 hover:text-zinc-300'}`}
                            data-testid={`admin-publish-toggle-${u.user_id}`}
                            title="Creators permitted here can publish games to the catalogue"
                          >
                            {u.can_publish ? 'Creator ✓' : 'Not permitted'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-400 hover:text-amber-300 text-xs"
                              onClick={async () => {
                                const np = window.prompt(`Reset password for ${u.email}?\nEnter new password (min 8 chars):`);
                                if (!np || np.length < 8) return;
                                try {
                                  await axios.put(`${API}/admin/users/${u.user_id}/password`, { password: np }, { withCredentials: true });
                                  toast.success('Password reset');
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Reset failed');
                                }
                              }}
                              data-testid={`admin-reset-pw-${u.user_id}`}
                              title="Reset password"
                            >
                              Reset PW
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-cyan-400 hover:text-cyan-300 text-xs"
                              onClick={async () => {
                                const ne = window.prompt(`Change email for ${u.email}?\nEnter new email:`, u.email);
                                if (!ne || ne === u.email || !ne.includes('@')) return;
                                try {
                                  await axios.put(`${API}/admin/users/${u.user_id}/email`, { email: ne }, { withCredentials: true });
                                  toast.success(`Email changed to ${ne}`);
                                  setUsers(users.map(x => x.user_id === u.user_id ? { ...x, email: ne } : x));
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Email change failed');
                                }
                              }}
                              data-testid={`admin-edit-email-${u.user_id}`}
                              title="Change email"
                            >
                              Edit Email
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'games' && (
            <div className="space-y-3">
              {/* Bulk regen action bar */}
              <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="admin-games-actions">
                <span className="text-xs font-mono text-zinc-500 uppercase">Bulk actions</span>
                <BulkRegenButton />
                <div className="ml-auto flex items-center gap-1" data-testid="admin-games-filter">
                  {[['all', 'All'], ['active', 'Active'], ['archived', `📦 Archived (${games.filter(g => g.archived).length})`]].map(([k, label]) => (
                    <button key={k} onClick={() => setGameFilter(k)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${gameFilter === k ? 'bg-[#06B6D4]/20 text-[#06B6D4]' : 'text-zinc-500 hover:text-white'}`}
                      data-testid={`admin-games-filter-${k}`}>{label}</button>
                  ))}
                </div>
              </div>
            <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#1E1E24]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Game</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Genre</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Published</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1E1E24]">
                    {filteredGames.map(g => (
                      <tr key={g.project_id} className="hover:bg-[#1E1E24]/50">
                        <td className="px-4 py-3">
                          <p className="text-sm text-white">{g.name}</p>
                          <p className="text-xs text-zinc-500">{g.project_id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-zinc-400 capitalize">{g.genre}</span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleGamePublished(g.project_id, !g.published)}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              g.published 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-zinc-700 text-zinc-400'
                            }`}
                          >
                            {g.published ? 'Published' : 'Private'}
                          </button>
                          {g.archived && <span className="ml-2 px-2 py-1 rounded text-xs font-medium bg-amber-500/15 text-amber-400" data-testid={`admin-archived-chip-${g.project_id}`}>📦 Archived</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => navigate(`/play/${g.project_id}`)}
                              className="text-zinc-400 hover:text-white"
                              title="Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/admin/games/${g.project_id}/editors_pick`,
                                    { pinned: !g.editors_pick },
                                    { withCredentials: true });
                                  setGames(games.map(x => x.editors_pick && x.project_id !== g.project_id ? { ...x, editors_pick: false } : x.project_id === g.project_id ? { ...x, editors_pick: !g.editors_pick } : x));
                                  toast.success(g.editors_pick ? 'Unpinned' : 'Pinned as Editor\u2019s Pick');
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              }}
                              className={`text-xs ${g.editors_pick ? 'text-pink-400 hover:text-pink-300' : 'text-zinc-500 hover:text-pink-300'}`}
                              data-testid={`admin-editors-pick-${g.project_id}`}
                              title="Toggle Editor's Pick (pins to Featured carousel)"
                            >
                              {g.editors_pick ? '★ Pinned' : '☆ Pin'}
                            </Button>
                            {g.editors_pick && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  const raw = window.prompt(`Auto-unpin "${g.name}" after how many days?\nLeave blank to clear the schedule.\nCurrent: ${g.featured_expires_at ? new Date(g.featured_expires_at).toLocaleString() : 'no schedule'}`, '7');
                                  if (raw === null) return;
                                  const days = raw.trim() === '' ? null : Number(raw);
                                  try {
                                    await axios.post(`${API}/admin/games/${g.project_id}/feature_schedule`,
                                      { expires_in_days: days }, { withCredentials: true });
                                    toast.success(days ? `Auto-unpin in ${days} days` : 'Schedule cleared');
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                                }}
                                className="text-xs text-amber-400 hover:text-amber-300"
                                data-testid={`admin-feature-schedule-${g.project_id}`}
                                title="Schedule auto-unpin"
                              >
                                ⏱
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                const current = (g.tags || []).join(',');
                                const next = window.prompt(`Comma-separated tag keys for "${g.name}"\n(e.g. staff_pick,featured,new,rising,must_play,hidden_gem)`, current);
                                if (next === null) return;
                                const tags = next.split(',').map(s => s.trim()).filter(Boolean);
                                try {
                                  await axios.post(`${API}/admin/games/${g.project_id}/tags`,
                                    { tags }, { withCredentials: true });
                                  setGames(games.map(x => x.project_id === g.project_id ? { ...x, tags } : x));
                                  toast.success('Tags updated');
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              }}
                              className="text-xs text-zinc-500 hover:text-[#06B6D4]"
                              data-testid={`admin-tags-${g.project_id}`}
                              title="Edit tags"
                            >
                              Tags{g.tags?.length ? ` (${g.tags.length})` : ''}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const r = await axios.put(`${API}/admin/games/${g.project_id}/archive`, {}, { withCredentials: true });
                                  setGames(games.map(x => x.project_id === g.project_id ? { ...x, archived: r.data.archived, published: r.data.archived ? false : x.published } : x));
                                  toast.success(r.data.archived ? `"${g.name}" archived — kept safe, out of the gallery` : `"${g.name}" restored — publish it to reintroduce`);
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              }}
                              className={`text-xs ${g.archived ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-500 hover:text-amber-300'}`}
                              data-testid={`admin-archive-toggle-${g.project_id}`}
                              title="Archive keeps the game reserved (never deleted, out of the gallery); Restore lets you reintroduce it"
                            >
                              {g.archived ? '♻ Restore' : '📦 Archive'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const r = await axios.put(`${API}/admin/games/${g.project_id}/template`, {}, { withCredentials: true });
                                  setGames(games.map(x => x.project_id === g.project_id ? { ...x, is_template: r.data.is_template } : x));
                                  toast.success(r.data.is_template ? `"${g.name}" promoted to Template` : `"${g.name}" removed from Templates`);
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              }}
                              className={`text-xs ${g.is_template ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-500 hover:text-amber-300'}`}
                              data-testid={`admin-template-toggle-${g.project_id}`}
                              title="Promote/remove this game as a reusable starter template"
                            >
                              {g.is_template ? '★ Template' : 'Template'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Delete "${g.name}"?\n\nIt is unpublished immediately and moved to Trash (restorable for 30 days).`)) return;
                                try {
                                  await axios.delete(`${API}/projects/${g.project_id}`, { withCredentials: true });
                                  setGames(games.filter(x => x.project_id !== g.project_id));
                                  toast.success(`"${g.name}" moved to trash`);
                                } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                              }}
                              className="text-xs text-zinc-500 hover:text-red-400"
                              data-testid={`admin-delete-game-${g.project_id}`}
                              title="Delete this game (any game — template, featured or wave)"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </div>
          )}

          {activeTab === 'tags' && (
            <div className="max-w-3xl">
              <AdminTagsManager />
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="max-w-2xl">
              <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Send Notification to All Users</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Title</label>
                    <Input
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                      placeholder="Notification title..."
                      className="bg-[#1E1E24] border-[#2E2E34] text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Message</label>
                    <textarea
                      value={notificationMessage}
                      onChange={(e) => setNotificationMessage(e.target.value)}
                      placeholder="Notification message..."
                      rows={4}
                      className="w-full bg-[#1E1E24] border border-[#2E2E34] rounded-md px-3 py-2 text-white resize-none"
                    />
                  </div>
                  <Button 
                    onClick={sendNotification} 
                    disabled={sendingNotification}
                    className="bg-[#06B6D4] text-black"
                  >
                    {sendingNotification ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send to All Users
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'flags' && <AdminFlags />}
          {activeTab === 'content' && <AdminContent />}
          {activeTab === 'promokit' && <AdminPromokit />}
          {activeTab === 'logokit' && <AdminLogokit />}
          {activeTab === 'trash' && <AdminTrash />}
          {activeTab === 'help' && <AdminHelp />}
          {activeTab === 'admins' && <AdminEmailsTab />}
          {activeTab === 'marketplace' && <AdminMarketplaceTab />}
          {activeTab === 'aikeys' && <AdminAIKeysTab />}
          {activeTab === 'hooks' && <AdminHooksTab />}
          {activeTab === 'telemetry' && <AdminTelemetryTab />}
          {activeTab === 'diagnostics' && <AdminDiagnosticsTab />}
        </main>
      </div>
    </div>
  );
}
